/**
 * Page Visibility → video pause, at the media plane.
 *
 * `usePageVisibility` already suspends the UI's tile plan; this is the backstop
 * that holds even if no component is mounted. A hidden tab holding nine 360p
 * subscriptions wastes ~2.5 Mbps of egress per viewer (ARCH §4).
 */
import { MEDIA_LIMITS } from '@cyphex/types';

const GRACE_MS = MEDIA_LIMITS.HIDDEN_TAB_VIDEO_TIMEOUT_S * 1000;

/** Calls `onChange(true)` once the tab has been hidden for the grace period. */
export function watchVisibility(onChange: (hidden: boolean) => void): () => void {
  let timer: number | null = null;

  const clear = (): void => {
    if (timer !== null) window.clearTimeout(timer);
    timer = null;
  };

  const evaluate = (): void => {
    clear();
    if (document.visibilityState === 'hidden') {
      timer = window.setTimeout(() => onChange(true), GRACE_MS);
      return;
    }
    onChange(false);
  };

  document.addEventListener('visibilitychange', evaluate);
  return () => {
    clear();
    document.removeEventListener('visibilitychange', evaluate);
  };
}
